import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import { TextField, ErrorBanner } from './fields';
import { verifyEmail, resendCode } from './authApi';

const secondsUntil = (sessionTimeOut) => {
  if (!sessionTimeOut) return 120;
  const diff = Math.ceil((sessionTimeOut - Date.now()) / 1000);
  return diff > 0 ? Math.min(diff, 120) : 0;
};

const VerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(() => secondsUntil(location.state?.sessionTimeOut));
  const timerRef = useRef(null);

  // Geri sayım
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const handleVerify = useCallback(
    async (e) => {
      e.preventDefault();
      if (loading) return;
      if (code.trim().length < 4) {
        setError(t('auth.error.required'));
        return;
      }
      setError(null);
      setInfo(null);
      setLoading(true);
      try {
        await verifyEmail({ email, code: code.trim() });
        navigate('/', { replace: true });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [loading, code, email, navigate, t]
  );

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    try {
      const data = await resendCode(email);
      setSeconds(secondsUntil(data?.verifyRequest?.sessionTimeOut));
      setInfo(t('verify.resent'));
    } catch (err) {
      setError(err.message);
    }
  };

  // E-posta bilgisi yoksa (doğrudan erişim) signup'a yönlendir.
  if (!email) {
    return <Navigate to="/signup" replace />;
  }

  return (
    <AuthShell
      eyebrow={t('verify.eyebrow')}
      title={t('verify.title')}
      subtitle={t('verify.subtitle', { email })}
      switchNode={
        <Link to="/">{t('verify.skip')}</Link>
      }
    >
      <form className="auth-form" onSubmit={handleVerify} noValidate>
        <ErrorBanner message={error} />
        {info && <div className="auth-info">{info}</div>}

        <TextField
          id="code"
          label={t('verify.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder={t('verify.code.ph')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="code-input"
          autoFocus
        />

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading && <span className="auth-spinner" />}
          {loading ? t('verify.verifying') : t('verify.action')}
        </button>

        <button
          type="button"
          className="auth-resend"
          onClick={handleResend}
          disabled={seconds > 0}
        >
          {seconds > 0 ? t('verify.resendIn', { seconds }) : t('verify.resend')}
        </button>
      </form>
    </AuthShell>
  );
};

export default VerifyEmail;
